import { Feature } from '@enums/feature.enum';
import { UseBaseHooks } from '@hooks/use-base.hooks';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UpgradeModal, useTranslation } from '@testsigma/atoms';
import { SessionContext } from '@contexts/session-context';
import NotFoundComponent from '@routes/error';
import { actionsType } from '@modules/settings/components/sidenav';

interface RouteGuardProps {
  children: React.ReactNode;
  access: boolean;
  featureName?: Feature;
}

const RouteGuard: React.FC<RouteGuardProps> = ({
  children,
  access = true,
  featureName,
}) => {
  const navigate = useNavigate();
  const { hasFeature } = UseBaseHooks();
  const { t } = useTranslation();
  const [showUgradePopup, setShowUpgradePopup] = useState<boolean>(false);
  const { session } = useContext(SessionContext);

  const actions: actionsType[] = [
    {
      type: 'primary',
      content: t('common.proceed'),
      callbackhandler: () => navigate('/settings/billing'),
      closePopup: true,
      isDisabled: !session?.user?.isAdmin,
    },
    {
      type: 'secondary',
      content: t('integrations.common.cancel'),
      callbackhandler: () => setShowUpgradePopup(false),
      closePopup: true,
      isDisabled: false,
    },
  ];

  const getUpgardeModalContent = useCallback(
    (featureName: Feature) => {
      return (
        <div className='w-125 flex-wrap flex'>
          <h3 role='heading' className={'text-xl pb-4 font-semibold'}>
            {t('common.plan_upgrade.title')}
          </h3>
          <div className='text-secondary-500'>
            <p className='mb-2 text-sm'>
              {t(`common.upgrade.${featureName}.message`)}
            </p>
            <p className='text-sm'>
              {!session?.user?.isAdmin && t('common.not_admin.message')}
            </p>
          </div>
        </div>
      );
    },
    [featureName],
  );

  useEffect(() => {
    if (!access) {
      navigate('/access_denied', { replace: true });
    } else if (featureName && !hasFeature(featureName)) {
      setShowUpgradePopup(true);
    }
  }, [access, featureName]);

  const renderComponent = () => {
    if (!featureName || (featureName && hasFeature(featureName))) {
      return children;
    } else {
      return <NotFoundComponent />;
    }
  };

  return showUgradePopup ? (
    <UpgradeModal
      show={showUgradePopup}
      onClose={setShowUpgradePopup}
      actions={actions}
      modalContent={getUpgardeModalContent(featureName)}
    />
  ) : (
    renderComponent()
  );
};

export default RouteGuard;
